/**
 * Offboarding: the second admin's inbox. Each request was asked from a
 * customer's panel with a reason and the rows it touches; someone other than
 * the requester approves (it runs at once) or rejects. Below, what happened
 * to earlier requests.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Badge, Button, PageHeader, formatDateTime } from '@lipaflow/ui'
import { useAuthStore } from '@lipaflow/shared'
import { adminService } from '../services/admin'
import { offboardError, offboarding, type OffboardRequest } from '../services/offboarding'
import { usePageTitle } from '../lib/pageTitle'
import { useSector } from '../sector/sector'

const STATUS: Record<string, { label: string; variant: 'warning' | 'success' | 'neutral' | 'clay' }> = {
  pending: { label: 'Waiting for a decision', variant: 'warning' },
  approved: { label: 'Approved, running', variant: 'warning' },
  executed: { label: 'Done', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'neutral' },
  failed: { label: 'Failed: nothing changed', variant: 'clay' },
}

function rowsTouched(radius: Record<string, number>): string {
  const total = Object.values(radius).reduce((sum, n) => sum + (Number(n) || 0), 0)
  const tables = Object.keys(radius).length
  return `${total.toLocaleString('en-KE')} records across ${tables} ${tables === 1 ? 'table' : 'tables'}`
}

export default function OffboardingPage() {
  usePageTitle('Offboarding')
  const queryClient = useQueryClient()
  const me = useAuthStore((s) => s.user?.id)
  const sector = useSector()
  const [notes, setNotes] = useState<Record<string, string>>({})
  const requests = useQuery({ queryKey: ['offboard-requests', sector.code], queryFn: () => offboarding.list(sector.code) })
  const customers = useQuery({ queryKey: ['tenants', sector.code], queryFn: () => adminService.listTenants(sector.code) })
  const staff = useQuery({ queryKey: ['admin-staff'], queryFn: () => adminService.listStaff() })

  const decide = useMutation({
    mutationFn: ({ req, approve }: { req: OffboardRequest; approve: boolean }) =>
      approve ? offboarding.approve(req.id, notes[req.id]?.trim() || undefined) : offboarding.reject(req.id, notes[req.id]?.trim() || undefined),
    onSuccess: (req) => {
      toast[req.status === 'failed' ? 'error' : 'success'](
        req.status === 'executed' ? 'Offboarded' : req.status === 'rejected' ? 'Rejected' : 'It failed; nothing changed',
      )
      queryClient.invalidateQueries({ queryKey: ['offboard-requests'] })
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    },
    onError: (e) => toast.error(offboardError(e, "Couldn't decide")),
  })

  const nameOf = (tenantId: string) => customers.data?.find((t) => t.id === tenantId)?.name ?? 'A customer'
  const person = (id: string | null) => (id ? staff.data?.find((s) => s.id === id)?.full_name ?? 'Another admin' : 'LipaFlow')
  const all = requests.data ?? []
  const open = all.filter((r) => r.status === 'pending')
  const past = all.filter((r) => r.status !== 'pending')

  return (
    <div className="space-y-4">
      <PageHeader
        title="Offboarding"
        subtitle={`Requests to close ${sector.name} customers. Someone other than the person who asked decides.`}
      />
      {requests.isLoading && <p role="status" className="text-body-sm text-ink-muted">Loading…</p>}
      {!requests.isLoading && open.length === 0 && (
        <p className="rounded-card border border-hairline bg-surface p-6 text-body-sm text-ink-muted">
          Nothing is waiting. Ask from a customer's panel under Customers.
        </p>
      )}

      <ul className="space-y-3" aria-label="Waiting for a decision">
        {open.map((req) => {
          const own = req.requested_by_user_id !== null && req.requested_by_user_id === me
          return (
            <li key={req.id} className="rounded-card border border-hairline bg-surface p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-body font-medium text-ink">
                    {req.mode === 'hard' ? 'Erase' : 'Deactivate'} {nameOf(req.tenant_id)}
                    {req.mode === 'hard' && <Badge variant="clay" size="sm" className="ml-2">Irreversible</Badge>}
                  </p>
                  <p className="text-body-sm text-ink">“{req.reason}”</p>
                  <p className="text-caption text-ink-muted">
                    {person(req.requested_by_user_id)} · {formatDateTime(req.requested_at)} · touches {rowsTouched(req.blast_radius)}
                  </p>
                </div>
                {own && <p className="text-caption text-ink-muted">You asked for this; someone else must decide.</p>}
              </div>
              {!own && (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="block flex-1 min-w-[12rem]">
                    <span className="block text-caption text-ink-muted mb-1">Note (optional)</span>
                    <input
                      value={notes[req.id] ?? ''}
                      onChange={(e) => setNotes((n) => ({ ...n, [req.id]: e.target.value }))}
                      className="w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </label>
                  <Button variant="secondary" disabled={decide.isPending} onClick={() => decide.mutate({ req, approve: false })}>Reject</Button>
                  <Button disabled={decide.isPending} onClick={() => decide.mutate({ req, approve: true })}>
                    {req.mode === 'hard' ? 'Approve and erase' : 'Approve and deactivate'}
                  </Button>
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {past.length > 0 && (
        <section aria-labelledby="offboard-history" className="space-y-2">
          <h3 id="offboard-history" className="text-caption text-ink-muted">Earlier requests</h3>
          <ul className="space-y-2">
            {past.map((req) => {
              const status = STATUS[req.status] ?? { label: req.status, variant: 'neutral' as const }
              return (
                <li key={req.id} className="flex flex-wrap items-start justify-between gap-2 rounded-card border border-hairline bg-surface px-4 py-3">
                  <div>
                    <p className="text-body-sm text-ink">{req.mode === 'hard' ? 'Erase' : 'Deactivate'} {nameOf(req.tenant_id)}</p>
                    <p className="text-caption text-ink-muted">
                      Asked by {person(req.requested_by_user_id)}; decided by {person(req.decided_by_user_id)}
                      {req.decided_at ? ` · ${formatDateTime(req.decided_at)}` : ''}
                      {req.decision_note ? ` · “${req.decision_note}”` : ''}
                    </p>
                  </div>
                  <Badge variant={status.variant} size="sm">{status.label}</Badge>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}
